import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight } from '@fortawesome/free-solid-svg-icons';

interface ShortlistProfile {
  id: string;
  title: string;
  experience: string;
  skills: string[];
  industries?: string[];
}

interface ShortlistBarProps {
  shortlistedProfiles: ShortlistProfile[];
  onRemoveFromShortlist: (profileId: string) => void;
  onViewShortlist: () => void;
}

const ShortlistBar: React.FC<ShortlistBarProps> = ({
  shortlistedProfiles,
  onRemoveFromShortlist,
  onViewShortlist,
}) => {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 p-4 z-50"
      style={{
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #E5E7EB',
        fontFamily: 'var(--dreamdev-font-family)',
        boxShadow: '0 -20px 25px -15px rgba(1, 35, 140, 0.15)'
      }}
    >
      <div className="max-w-8xl mx-auto">
        {/* Title and Subtitle with Submit Button */}
        <div className="flex items-center justify-between mb-4" style={{ gap: '10px' }}>
          <div>
            <div className="flex items-center mb-1">
              <h3
                className="font-bold"
                style={{
                  fontSize: '18px',
                  color: shortlistedProfiles.length === 0 ? '#697586' : '#FF5C3E',
                  fontWeight: '700',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                Your shortlist ({shortlistedProfiles.length})
              </h3>
              <span
                className="mx-2"
                style={{
                  color: '#697586',
                  fontSize: '16px'
                }}
              >
                •
              </span>
              <span
                style={{
                  color: '#697586',
                  fontSize: '16px',
                  fontWeight: '400',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {shortlistedProfiles.length === 0
                  ? "Technical interviews arranged within 24 hours"
                  : "Continue to share your shortlist and provide contact details — our team will confirm availability and align next steps."
                }
              </span>
            </div>
          </div>

          {/* Submit Button - Always visible */}
          <button
            onClick={shortlistedProfiles.length > 0 ? onViewShortlist : undefined}
            disabled={shortlistedProfiles.length === 0}
            className="px-4 py-2 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200"
            style={{
              backgroundColor: shortlistedProfiles.length === 0 ? '#9CA3AF' : '#0034E6',
              color: '#FFFFFF',
              fontSize: 'var(--dreamdev-font-size-sm)',
              fontWeight: '700',
              borderRadius: '6px',
              border: shortlistedProfiles.length === 0 ? '1px solid #9CA3AF' : '1px solid #0034E6',
              cursor: shortlistedProfiles.length === 0 ? 'not-allowed' : 'pointer',
              opacity: shortlistedProfiles.length === 0 ? 0.6 : 1
            }}
            onMouseEnter={(e: any) => {
              if (shortlistedProfiles.length > 0) {
                e.currentTarget.style.backgroundColor = '#03238C';
              }
            }}
            onMouseLeave={(e: any) => {
              if (shortlistedProfiles.length > 0) {
                e.currentTarget.style.backgroundColor = '#0034E6';
              }
            }}
          >
            {shortlistedProfiles.length === 0 ? (
              <span>ADD TO CONTINUE</span>
            ) : (
              <>
                <span>CONTINUE</span>
                <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Shortlist Cards */}
          <div className="flex-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            <div className="flex space-x-3 pb-2" style={{ minWidth: 'max-content' }}>
              {shortlistedProfiles.length === 0 ? (<></>) : (
                shortlistedProfiles.map((profile) => (
                  <div
                    key={profile.id}
                    className="flex-shrink-0 rounded-lg p-3 min-w-48 sm:min-w-64"
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px'
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4
                          className="font-medium text-sm truncate mb-1"
                          style={{
                            color: '#03238C',
                            fontSize: 'var(--dreamdev-font-size-sm)',
                            fontWeight: '700',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          {profile.title}
                        </h4>
                        <p
                          className="text-xs mb-2"
                          style={{
                            color: '#6B7280',
                            fontSize: 'var(--dreamdev-font-size-xs)'
                          }}
                        >
                          {profile.experience}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {profile.industries && profile.industries.length > 0 ? (
                            <>
                              {profile.industries.slice(0, 3).map((industry, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 text-xs rounded-full"
                                  style={{
                                    backgroundColor: '#CEECFF',
                                    color: '#03238C',
                                    fontSize: 'var(--dreamdev-font-size-xs)',
                                    fontWeight: '400',
                                    fontFamily: 'Source Sans 3, sans-serif'
                                  }}
                                >
                                  {industry}
                                </span>
                              ))}
                              {profile.industries.length > 3 && (
                                <span
                                  className="px-2 py-1 text-xs rounded-full"
                                  style={{
                                    backgroundColor: '#CEECFF',
                                    color: '#03238C',
                                    fontSize: 'var(--dreamdev-font-size-xs)',
                                    fontWeight: '400',
                                    fontFamily: 'Source Sans 3, sans-serif'
                                  }}
                                >
                                  +{profile.industries.length - 3}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {profile.skills.slice(0, 3).map((skill, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 text-xs rounded-full"
                                  style={{
                                    backgroundColor: '#CEECFF',
                                    color: '#03238C',
                                    fontSize: 'var(--dreamdev-font-size-xs)',
                                    fontWeight: '400',
                                    fontFamily: 'Source Sans 3, sans-serif'
                                  }}
                                >
                                  {skill}
                                </span>
                              ))}
                              {profile.skills.length > 3 && (
                                <span
                                  className="px-2 py-1 text-xs rounded-full"
                                  style={{
                                    backgroundColor: '#CEECFF',
                                    color: '#03238C',
                                    fontSize: 'var(--dreamdev-font-size-xs)',
                                    fontWeight: '400',
                                    fontFamily: 'Source Sans 3, sans-serif'
                                  }}
                                >
                                  +{profile.skills.length - 3}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveFromShortlist(profile.id)}
                        className="ml-2 transition-colors duration-200 font-bold uppercase"
                        style={{
                          color: '#6B7280'
                        }}
                        onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = '#03238C'}
                        onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = '#6B7280'}
                      >
                        <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortlistBar;
